import { cn } from '../../../utils'

export interface ReadOnlyTextProps {
  value?: string | number | boolean | null
  label: string
  className?: string
  variant?: 'semitransparent' | 'white' | 'solid' | 'outline'
  activateEmptyWarning?: boolean
  isMultiline?: boolean
  'data-testid': string
}

export default function ReadOnlyText({
  value,
  label,
  className,
  variant = 'solid',
  activateEmptyWarning = false,
  isMultiline = false,
  'data-testid': testId
}: Readonly<ReadOnlyTextProps>) {
  const isEmpty = !value || String(value).trim() === ''
  const hasLabel = label && label.trim() !== ''

  return (
    <div
      className={cn(
        className,
        activateEmptyWarning && isEmpty && 'ring-2 ring-red-400 rounded-md'
      )}
      data-testid={`${testId}-wrapper`}
      aria-readonly="true"
    >
      <div className="relative">
        <div
          className={cn(
            'block w-full',
            !isMultiline && ['min-h-[44px]', 'overflow-visible'],

            isMultiline && 'min-h-[88px]', // Min height for multiline (2x single line)
            'px-1',
            'py-0.5',
            'text-base',
            'leading-[24px]',
            'rounded',
            'border-b',
            variant === 'semitransparent' && [
              'bg-gray-800/5',
              'border-b-gray-800/50',
              'dark:bg-cool-gray-100/5',
              'dark:border-b-cool-gray-100/50'
            ],
            variant === 'white' && [
              'bg-white',
              'border-b-gray-800/50',
              'dark:bg-cool-gray-800',
              'dark:border-b-cool-gray-100/50'
            ],
            variant === 'solid' && [
              'bg-cool-gray-100',
              'border-b-gray-800/50',
              'dark:bg-cool-gray-700',
              'dark:border-b-cool-gray-100/50'
            ],
            variant === 'outline' && [
              'bg-transparent',
              'border border-gray-800/50',
              'dark:border-cool-gray-100/50'
            ],

            // Text color
            'text-cool-gray-700',
            'dark:text-cool-gray-200',

            // States
            'pointer-events-none',
            'cursor-default',

            // Flex container for proper vertical alignment
            !isMultiline && 'flex items-center',

            // Multiline specific styles
            isMultiline && [
              'overflow-y-auto' // Scroll if needed
            ],

            // Position relative for floating label
            hasLabel && 'pt-[20px]', // Extra padding top for label space

            // CSS trick for baseline alignment when empty
            isEmpty &&
              !isMultiline &&
              "after:content-['\u00A0'] after:invisible"
          )}
          data-variant={variant}
          data-testid={testId}
          role="text"
          aria-readonly="true"
          aria-labelledby={hasLabel ? `${testId}-label` : undefined}
        >
          {value == null ? '' : String(value)}
        </div>
        {hasLabel && (
          <label
            className={cn(
              'absolute',
              'left-1',
              'top-[2px]',
              'text-xs',
              'text-cool-gray-500',
              'dark:text-cool-gray-200',
              'pointer-events-none',
              'transition-all duration-250'
            )}
            htmlFor={testId}
            id={`${testId}-label`}
            data-testid={`${testId}-label`}
          >
            {label}
          </label>
        )}
      </div>
    </div>
  )
}
